"use client";

import { useState } from "react";

import { cn } from "@/shared/lib/utils/cn";
import { Button } from "@/shared/ui/shadcn-components/button";
import type { InsightsUserQuestion } from "../types";

export function InsightsUserQuestionCard({
  pending,
  onSubmit,
  disabled,
}: {
  pending: InsightsUserQuestion;
  onSubmit: (answer: string | string[]) => void;
  disabled?: boolean;
}) {
  const multi = pending.selectionMode === "multi_select";
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedMany, setSelectedMany] = useState<string[]>([]);
  const [custom, setCustom] = useState("");

  const answerSingle = custom.trim() || selected;
  const canSubmit = multi
    ? selectedMany.length > 0 && !disabled
    : Boolean(answerSingle) && !disabled;

  const toggleMany = (value: string) => {
    setSelectedMany((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  return (
    <div className="rounded-tl-md rounded-tr-2xl rounded-b-2xl border border-border/70 bg-card p-4 shadow-sm">
      <p className="mb-3 text-[15px] font-medium leading-6 text-foreground">{pending.question}</p>
      <div className="space-y-2">
        {pending.options.map((opt) => {
          const value = opt.value ?? opt.label;
          const isSelected = multi
            ? selectedMany.includes(value)
            : selected === value && !custom.trim();
          const criteria = opt.selection_criteria;
          const showFrame = pending.purpose === "definition" || pending.purpose === "plan_confirm";
          return (
            <button
              key={value}
              type="button"
              disabled={disabled}
              onClick={() => {
                if (multi) {
                  toggleMany(value);
                  return;
                }
                setCustom("");
                setSelected(value);
              }}
              className={cn(
                "flex w-full flex-col items-start gap-1 rounded-xl border px-3 py-2 text-left text-[13px] transition-colors",
                isSelected
                  ? "border-primary bg-accent text-foreground"
                  : "border-border/70 hover:bg-muted/50",
              )}
            >
              <span className="flex w-full items-center gap-2">
                <span
                  className={cn(
                    multi
                      ? "size-3.5 shrink-0 rounded-sm border-2"
                      : "size-3.5 shrink-0 rounded-full border-2",
                    isSelected ? "border-primary bg-primary" : "border-muted-foreground/40",
                  )}
                />
                {opt.label}
              </span>
              {showFrame && (opt.definition || criteria) ? (
                <span className="w-full space-y-0.5 pl-5 text-[11px] leading-4 text-muted-foreground">
                  {opt.definition ? <span className="block">{opt.definition}</span> : null}
                  {criteria?.population ? (
                    <span className="block">Population: {criteria.population}</span>
                  ) : null}
                  {criteria?.period ? (
                    <span className="block">Period: {criteria.period}</span>
                  ) : null}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {!multi ? (
        <input
          type="text"
          value={custom}
          disabled={disabled}
          placeholder="Or type your own answer…"
          onChange={(e) => {
            setCustom(e.target.value);
            if (e.target.value) setSelected(null);
          }}
          className="mt-3 w-full rounded-xl border border-border/70 bg-background px-3 py-2 text-[13px] outline-none focus:border-primary"
        />
      ) : null}
      <div className="mt-3 flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={!canSubmit}
          onClick={() => {
            if (multi) {
              if (selectedMany.length) onSubmit(selectedMany);
              return;
            }
            if (answerSingle) onSubmit(answerSingle);
          }}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
